import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { buildPlaylist, annotateFirstNames, memberAt, transcript, fmt } from './usePlaylist';
import CastEditor, { CAST_CSS } from './CastEditor';
import Download from './Download';
import TopUp, { TOPUP_CSS } from './TopUp';
import { Picture, PictureViewer, pickPicture, nextPictureUrl, PICTURE_CSS } from './ScenePicture';

/**
 * Table read player (ALPHA) — SELF-CONTAINED. It fills whatever box it is given (a full-viewport
 * page or a full-height overlay) and owns its own scrolling: header / script (scrolls) / dock.
 * Nothing is position:fixed to the window — that is what let the site footer paint over the dock.
 *
 * Reads, relative to `base` (hash-keyed; internal ids never reach here):
 *   GET {base}/scenes             scene list + read_id + render progress
 *   GET {base}/scene/{n}/recipe   one scene's recipe (tableread/RECIPE_CONTRACT.md)
 *
 * READ ID: the server's opaque id for the generation being served. Recipes are cached per read id,
 * and audio URLs embed it, so after a re-render (cast edit, new renderer) nothing stale can play:
 * an old audio URL answers 410, the player refetches the scene list, drops the cache and reloads
 * the scene at the same position (spec §6.1).
 *
 * PLAYBACK MODEL: one element plays, one prefetches, a single play INTENT drives both; exactly one
 * place assigns src and one applies offsets (see git history for the seek bug this replaced).
 */
/*
 * App props (Android app, mobile/): `initialMs` resumes inside `initialScene`; `onPosition({scene, ms,
 * readId, playing, finished})` reports the listener's place every ~5 s, on scene change, on play/pause
 * and once at the end of the read — the app saves it as "Continue listening". The web passes neither.
 */
export default function TableReadPlayer({ base, initialScene, initialMs, autoPlay, onPosition, feedbackUrl, backUrl, onClose, scriptTitle, isDemo, shareId }) {
  const [scenes, setScenes] = useState([]);
  const [readId, setReadId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [sceneNo, setSceneNo] = useState(initialScene ?? null);
  const [recipe, setRecipe] = useState(null);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [settings, setSettings] = useState({ narration: 'full', speaker_names: 'first_per_scene' });
  const [autoNext, setAutoNext] = useState(true);
  const [idx, setIdx] = useState(0);
  // autoPlay: opened by a 'Listen' click (landing page). A browser that still blocks it gets the
  // normal Play button (NotAllowedError below), never a 'playing' player that is silent.
  const [wantPlay, setWantPlay] = useState(!!autoPlay);
  const [posMs, setPosMs] = useState(0);
  const [rate, setRate] = useState(1);
  const [follow, setFollow] = useState(true);
  const [fbOpen, setFbOpen] = useState(false);
  const [castOpen, setCastOpen] = useState(false);
  const [tick, setTick] = useState(0);              // drives the post-edit watch below
  const [canEditCast, setCanEditCast] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  const [expired, setExpired] = useState(false);
  const [retentionDays, setRetentionDays] = useState(60);
  const [access, setAccess] = useState(null);           // what may be heard, what the rest costs
  const [buy, setBuy] = useState(null);                 // the unlock sheet: {scene} (0 = whole script)
  const [preview, setPreview] = useState(null);         // {scenes, total_scenes} when this is a preview read
  const [images, setImages] = useState(null);           // {poster, stills} — see ScenePicture.jsx
  const [badPics, setBadPics] = useState(() => new Set());
  const [picOpen, setPicOpen] = useState(false);
  // Narrow screens show the alpha notice as a row under the header; the viewer can dismiss it (the
  // ALPHA tag in the header brings it back). Wide screens show it in the side column instead.
  const [setOpen, setSetOpen] = useState(false);        // phones: settings row folded behind a button
  const [alphaHidden, setAlphaHidden] = useState(() => { try { return localStorage.getItem('tr-alpha-hidden') === '1'; } catch (_) { return false; } });
  const hideAlpha = (v) => { setAlphaHidden(v); try { v ? localStorage.setItem('tr-alpha-hidden', '1') : localStorage.removeItem('tr-alpha-hidden'); } catch (_) {} };

  const lockedCount = access?.locked?.length ?? 0;

  const audio = useRef(null), prefetch = useRef(null);
  const pendingOffset = useRef(0), gapTimer = useRef(null), lastGlobal = useRef(0);
  const scroller = useRef(null), rowRefs = useRef({});
  const recipeCache = useRef(new Map());
  const readRef = useRef(null);
  const retried = useRef(null);            // audio_url already retried once
  const failStreak = useRef(0);            // consecutive lines that would not load
  const resumeAt = useRef(initialMs ?? null); // global ms to restore (after a read change, or the app's saved spot)
  const posRef = useRef(onPosition); useEffect(() => { posRef.current = onPosition; }, [onPosition]);

  /* ---------------- listening stats (tr_listens) ----------------
     Per player session and scene: lines heard to the end + ms of audio played. Reported as
     CUMULATIVE counts (the server keeps the max), every 15 s, on scene change and on close. */
  const sessionId = useRef(Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join(''));
  const heard = useRef({});                // scene -> { lines, ms }
  const dirty = useRef(false), lastT = useRef(null);
  const bump = (n, key, v) => {
    if (n == null) return;
    const c = heard.current[n] ?? (heard.current[n] = { lines: 0, ms: 0 });
    c[key] += v; dirty.current = true;
  };
  // Funnel (tr_events): did they get to the end of the free scenes, open the sheet, run short?
  const seen = useRef(new Set());
  const track = useCallback((kind, extra = {}) => {
    if (isDemo) return;
    // Once per player session per (kind, detail): a funnel step, not a click stream.
    const key = `${kind}|${extra.detail ?? ''}`;
    if (seen.current.has(key)) return;
    seen.current.add(key);
    axios.post(`${base}/event`, { kind, session: sessionId.current, ...extra }).catch(() => {});
  }, [base, isDemo]);

  // Opened at all, and pressed play at all: "opened and never played" is the clearest disinterest.
  useEffect(() => { track('opened'); }, [track]);

  const flush = useCallback((beacon = false) => {
    if (isDemo || !dirty.current) return;
    dirty.current = false;
    const body = JSON.stringify({ session: sessionId.current, read_id: readRef.current, share_id: shareId || '', scenes: heard.current });
    if (beacon) {
      // keepalive fetch survives page close and, unlike sendBeacon, can carry the CSRF header
      const xsrf = decodeURIComponent((document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/) || [])[1] || '');
      try { fetch(`${base}/listen`, { method: 'POST', keepalive: true, credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': xsrf, 'X-Requested-With': 'XMLHttpRequest' }, body }); } catch (_) {}
    } else {
      axios.post(`${base}/listen`, JSON.parse(body)).catch(() => { dirty.current = true; });
    }
  }, [base, isDemo, shareId]);
  useEffect(() => {
    const t = setInterval(() => flush(false), 15000);
    const onHide = () => flush(true);
    addEventListener('pagehide', onHide);
    return () => { clearInterval(t); removeEventListener('pagehide', onHide); flush(true); };
  }, [flush]);

  /* ---------------- data ---------------- */
  const loadScenes = useCallback(() => axios.get(`${base}/scenes`).then((r) => {
    const list = r.data.scenes ?? [];
    const rid = r.data.read_id ?? null;
    if (readRef.current && rid !== readRef.current) {
      // The served read changed under us: drop every cached recipe and reload this scene in place.
      recipeCache.current.clear();
      resumeAt.current = lastGlobal.current;
      setRecipe(null);
    }
    readRef.current = rid;
    setReadId(rid); setScenes(list); setProgress(r.data.progress ?? null); setCanEditCast(!!r.data.can_edit_cast);
    setCanDownload(!!r.data.can_download); setExpired(!!r.data.expired); if (r.data.retention_days) setRetentionDays(r.data.retention_days);
    setImages(r.data.images ?? null); setPreview(r.data.preview ?? null); setAccess(r.data.access ?? null);
    return { list, rid };
  }), [base]);

  useEffect(() => {
    loadScenes().then(({ list }) => {
      if (sceneNo == null || !list.find((s) => s.number === sceneNo && s.rendered)) {
        const first = list.find((s) => s.rendered);
        if (first) setSceneNo(first.number);
      }
    }).catch((e) => setErr(e?.response?.data?.error ?? e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  // While scenes are still rendering, refresh the list so new ones become playable.
  const stillRendering = !!(progress && (progress.building || progress.newer_building));
  useEffect(() => {
    if (!stillRendering) return undefined;
    const t = setInterval(() => {
      loadScenes().then(({ list }) => {
        if (sceneNo == null) { const first = list.find((s) => s.rendered); if (first) setSceneNo(first.number); }
      }).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [stillRendering, loadScenes, sceneNo]);

  /**
   * A cast edit or a re-record returns BEFORE its detached process has created the generation
   * (createGeneration parses, casts and pins first), so the one refresh those actions do can easily
   * report the old read and the poll above never starts — leaving "the read updates by itself" a
   * lie until a reload (review 2026-09-23, finding 7). After either action we watch for the new
   * generation ourselves, every 5 s for 2 minutes, and say so if it never appears.
   */
  const [watchUntil, setWatchUntil] = useState(0);
  const watchFrom = useRef(null);                                 // the read id we are waiting to leave
  const startWatch = useCallback(() => { watchFrom.current = readRef.current; setWatchUntil(Date.now() + 120000); }, []);
  useEffect(() => {
    if (!watchUntil) return undefined;
    // Either the new read is already here, or it is rendering and the 20 s poll above owns it.
    if (stillRendering || readId !== watchFrom.current) { setWatchUntil(0); return undefined; }
    if (Date.now() > watchUntil) {
      setWatchUntil(0);
      setNotice('The new recording has not started yet. Reload the page in a minute — or tell us via Send feedback if it stays like this.');
      return undefined;
    }
    const t = setTimeout(() => { loadScenes().catch(() => {}); setTick((n) => n + 1); }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchUntil, stillRendering, readId, tick, loadScenes]);

  const fetchRecipe = useCallback((n) => {
    const key = `${readRef.current}:${n}`;
    if (recipeCache.current.has(key)) return recipeCache.current.get(key);
    const p = axios.get(`${base}/scene/${n}/recipe`).then((r) => annotateFirstNames(r.data));
    recipeCache.current.set(key, p);
    p.catch(() => recipeCache.current.delete(key));
    return p;
  }, [base]);

  useEffect(() => { flush(false); }, [sceneNo, flush]);

  useEffect(() => {
    if (sceneNo == null || !readId) return undefined;
    let live = true;
    fetchRecipe(sceneNo).then((r) => {
      if (!live) return;
      clearTimeout(gapTimer.current); gapTimer.current = null;
      lastGlobal.current = 0; pendingOffset.current = 0;
      setRecipe(r); setIdx(0); setPosMs(0); setErr(null);
      if (scroller.current && resumeAt.current == null) scroller.current.scrollTop = 0;
    }).catch((e) => live && setErr(e?.response?.data?.error ?? e.message));
    return () => { live = false; };
  }, [sceneNo, readId, fetchRecipe]);

  const renderedList = useMemo(() => scenes.filter((s) => s.rendered), [scenes]);
  const nextScene = useMemo(() => {
    const i = renderedList.findIndex((s) => s.number === sceneNo);
    return i >= 0 ? renderedList[i + 1] ?? null : null;
  }, [renderedList, sceneNo]);

  /* ---------------- picture ---------------- */
  const pick = useMemo(() => pickPicture(images, sceneNo, badPics), [images, sceneNo, badPics]);
  const markBad = useCallback((u) => setBadPics((b) => (b.has(u) ? b : new Set(b).add(u))), []);
  useEffect(() => {                        // load the next scene's picture before it is needed
    const u = nextPictureUrl(images, sceneNo, badPics);
    if (u) { const im = new Image(); im.src = u; }
  }, [images, sceneNo, badPics]);
  const canGoToPick = !!(pick && pick.held && scenes.find((s) => s.number === pick.scene && s.rendered));

  /* ---------------- playlist ---------------- */
  const { items, total_ms } = useMemo(
    () => (recipe ? buildPlaylist(recipe, settings) : { items: [], total_ms: 0 }), [recipe, settings]);
  const rows = useMemo(() => transcript(items), [items]);
  const seg = items[idx] ?? null;
  const member = seg ? memberAt(seg, posMs) : null;
  const globalMs = seg ? seg.global_start_ms + posMs : 0;
  useEffect(() => { lastGlobal.current = globalMs; }, [globalMs]);

  const announcing = seg?.kind === 'speaker_name' && !!items[idx + 1];
  const activeIdx = announcing ? idx + 1 : idx;
  const activeMember = announcing ? (items[idx + 1].members?.[0] ?? member) : member;
  const heading = useMemo(() => {
    for (let i = activeIdx; i >= 0; i--) { const m = items[i]?.members?.[0]; if (m?.role === 'heading') return m.text; }
    return recipe?.title ?? '';
  }, [items, activeIdx, recipe]);
  const activeKey = useMemo(() => {
    const r = rows.find((x) => activeMember && x.text === activeMember.text && x.segIndex === activeIdx);
    return r?.key ?? null;
  }, [rows, activeMember, activeIdx]);

  /* ---------------- audio ---------------- */
  // Mirrors, so the source effect can read play intent and speed without depending on them.
  const wantPlayRef = useRef(wantPlay); useEffect(() => { wantPlayRef.current = wantPlay; }, [wantPlay]);
  const rateRef = useRef(rate); useEffect(() => { rateRef.current = rate; }, [rate]);

  const applyOffset = useCallback((el, sec) => {
    if (!el) return;
    const set = () => { try { el.currentTime = sec; } catch (_) {} };
    if (el.readyState >= 1) set(); else el.addEventListener('loadedmetadata', set, { once: true });
  }, []);

  /**
   * SOURCE ONLY. This effect seeks (applyOffset), so anything in its dependency list becomes a
   * rewind: it used to depend on `rate` and `wantPlay`, so changing speed or pressing pause jumped
   * back to the start of the current clip (review 2026-09-23, finding 5). Speed and play intent are
   * handled where they happen — `rate` in its own effect below, play/pause in `toggle` — and the
   * segment's URL plus its index are the only things that may move the playhead.
   */
  useEffect(() => {                      // assigns src (the error-retry path is the only other place)
    const el = audio.current; if (!el || !seg) return undefined;
    if (el.dataset.segId !== seg.audio_url) { el.src = seg.audio_url; el.dataset.segId = seg.audio_url; el.load(); }
    el.playbackRate = rateRef.current;
    applyOffset(el, pendingOffset.current); pendingOffset.current = 0;
    let go = null;
    if (wantPlayRef.current) {
      go = () => el.play().catch((e) => { if (e?.name === 'NotAllowedError') setWantPlay(false); });
      el.readyState >= 2 ? go() : el.addEventListener('canplay', go, { once: true });
    }
    return () => { if (go) el.removeEventListener('canplay', go); };   // never let a stale one fire
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, seg?.audio_url, applyOffset]);

  useEffect(() => { if (audio.current) audio.current.playbackRate = rate; }, [rate]);

  useEffect(() => {                      // preload the next clip; fetch the next scene's recipe early
    const ahead = items[idx + 1];
    if (ahead && prefetch.current && prefetch.current.dataset.segId !== ahead.audio_url) {
      prefetch.current.src = ahead.audio_url; prefetch.current.dataset.segId = ahead.audio_url; prefetch.current.load();
    }
    if (nextScene && idx >= items.length - 3) fetchRecipe(nextScene.number).catch(() => {});
  }, [idx, items, nextScene, fetchRecipe]);

  const seekTo = useCallback((ms) => {
    if (!items.length) return;
    const clamped = Math.max(0, Math.min(ms, total_ms - 1));
    const t = items.find((s) => clamped < s.global_end_ms) ?? items[items.length - 1];
    const into = Math.max(0, clamped - t.global_start_ms) / 1000;
    clearTimeout(gapTimer.current); gapTimer.current = null;
    setPosMs(into * 1000);
    if (t.index === idx) { applyOffset(audio.current, into); if (wantPlay) audio.current?.play().catch(() => {}); }
    else { pendingOffset.current = into; setIdx(t.index); }
  }, [items, total_ms, idx, wantPlay, applyOffset]);

  // After a read change, restore the listener's place once the new recipe is in.
  useEffect(() => {
    if (resumeAt.current == null || !items.length) return;
    const at = resumeAt.current; resumeAt.current = null;
    seekTo(at);
  }, [items, seekTo]);

  const jumpSegment = useCallback((delta) => {
    const t = items[idx + delta];
    if (t) { seekTo(t.global_start_ms); return; }
    if (delta > 0 && nextScene) setSceneNo(nextScene.number);
  }, [items, idx, seekTo, nextScene]);

  const freeDone = useRef(false);
  const advance = useCallback(() => {
    const ahead = items[idx + 1];
    if (!ahead) {
      if (autoNext && nextScene) { gapTimer.current = setTimeout(() => { gapTimer.current = null; setSceneNo(nextScene.number); }, 900 / rate); return; }
      // Heard the last scene they have to its end. THIS is "they finished the free part", not
      // whether the end card happened to scroll into view.
      if (!freeDone.current && lockedCount > 0) { freeDone.current = true; track('free_done', { scene: sceneNo }); }
      if (!nextScene) posRef.current?.({ scene: sceneNo, ms: 0, readId: readRef.current, playing: false, finished: true });
      setWantPlay(false); setPosMs(seg?.duration_ms ?? 0); return;
    }
    const gap = seg?.gap_after_ms ?? 0;
    const go = () => { gapTimer.current = null; pendingOffset.current = 0; setPosMs(0); setIdx(idx + 1); };
    gap > 0 ? (gapTimer.current = setTimeout(go, gap / rate)) : go();
  }, [idx, items, seg, rate, autoNext, nextScene, lockedCount, sceneNo, track]);

  // A take failed to load. Once: ask the server whether the read moved on (410) and reload in place,
  // or simply retry the same URL. Twice: skip the line and say so — never stall silently.
  const onAudioError = useCallback(() => {
    const el = audio.current; if (!el || !seg) return;
    const url = seg.audio_url;
    // Not playing (the preload of the first line, or a paused read): do nothing yet. Skipping and
    // counting here ran the whole give-up path before anyone pressed Play, and left an errored element
    // that play() cannot restart — the player then sat on "Pause" in silence. Play (toggle) reloads it.
    if (!wantPlay) return;
    if (retried.current !== url) {
      retried.current = url;
      const at = lastGlobal.current;
      loadScenes().then(({ rid }) => {
        if (rid !== (recipe?.read_id ?? rid)) return;      // read moved on: loadScenes reloads the scene in place
        el.src = url; el.dataset.segId = url; el.load();     // same read: plain retry, same position
        applyOffset(el, Math.max(0, at - seg.global_start_ms) / 1000);
        if (wantPlay) el.addEventListener('canplay', () => el.play().catch(() => {}), { once: true });
      }).catch(() => {});
      return;
    }
    failStreak.current += 1;
    if (failStreak.current >= 3) {
      // Not one bad file: this browser cannot play the audio, or the server is failing. Stop rather
      // than race through the scene skipping every line.
      el.pause(); setWantPlay(false);
      setNotice('The audio could not be played. Try reloading the page, or another browser — and please tell us via Send feedback.');
      return;
    }
    setNotice('One line could not be loaded and was skipped.');
    advance();
  }, [seg, recipe, wantPlay, loadScenes, advance, applyOffset]);

  const toggle = useCallback(() => {
    const el = audio.current; if (!el) return;
    if (wantPlay) { el.pause(); clearTimeout(gapTimer.current); gapTimer.current = null; setWantPlay(false); }
    else {
      el.playbackRate = rate; setWantPlay(true); track('play', { scene: sceneNo });
      failStreak.current = 0;                    // a fresh press gets the full retry-then-skip treatment
      if (seg && (el.error || el.networkState === 3)) {
        // The element already failed (missing take, expired URL): play() on it does nothing and fires no
        // events. Load the line again; its errors now go through onAudioError with play intent set.
        retried.current = null;
        el.src = seg.audio_url; el.dataset.segId = seg.audio_url; el.load();
        el.addEventListener('canplay', () => el.play().catch(() => {}), { once: true });
      }
      el.play().catch(() => {});                 // in the gesture either way (Safari unlocks the element)
    }
  }, [wantPlay, rate, track, sceneNo, seg]);

  useEffect(() => {
    if (!items.length) return;
    const t = items.find((s) => lastGlobal.current < s.global_end_ms) ?? items[0];
    if (t.index !== idx) { pendingOffset.current = 0; setIdx(t.index); setPosMs(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => () => { clearTimeout(gapTimer.current); audio.current?.pause(); }, []);
  useEffect(() => { if (!notice) return undefined; const t = setTimeout(() => setNotice(null), 6000); return () => clearTimeout(t); }, [notice]);

  /* ---------------- follow-along scrolling ---------------- */
  const scrollToActive = useCallback((behavior = 'smooth') => {
    const box = scroller.current, el = activeKey && rowRefs.current[activeKey];
    if (!box || !el) return;
    const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - box.clientHeight * 0.35;
    box.scrollTo({ top: Math.max(0, top), behavior });
  }, [activeKey]);
  useEffect(() => { if (follow) scrollToActive(); }, [activeKey, follow, scrollToActive]);
  useEffect(() => {
    const box = scroller.current; if (!box) return undefined;
    const stop = () => setFollow(false);
    box.addEventListener('wheel', stop, { passive: true });
    box.addEventListener('touchmove', stop, { passive: true });
    return () => { box.removeEventListener('wheel', stop); box.removeEventListener('touchmove', stop); };
  }, [recipe]);

  /* ---------------- the app's saved spot ---------------- */
  // Never before a pending resume has been applied: that would save "start of scene" over the real spot.
  const posBucket = Math.floor(globalMs / 5000);
  useEffect(() => {
    if (!posRef.current || sceneNo == null || !recipe || resumeAt.current != null) return;
    posRef.current({ scene: sceneNo, ms: Math.round(lastGlobal.current), readId: readRef.current, playing: wantPlay, finished: false });
  }, [sceneNo, posBucket, wantPlay, recipe]);

  /* ---------------- media session + keys ---------------- */
  useEffect(() => {
    if (!('mediaSession' in navigator) || !recipe) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: heading || 'Table read', artist: activeMember?.character ?? scriptTitle ?? 'Table read', album: scriptTitle ?? 'ScriptReader',
        artwork: pick ? [{ src: pick.url, sizes: '1024x1024' }] : [] });
      navigator.mediaSession.setActionHandler('play', toggle);
      navigator.mediaSession.setActionHandler('pause', toggle);
      navigator.mediaSession.setActionHandler('previoustrack', () => jumpSegment(-1));
      navigator.mediaSession.setActionHandler('nexttrack', () => jumpSegment(1));
    } catch (_) {}
  }, [recipe, heading, activeMember, scriptTitle, toggle, jumpSegment, pick]);

  useEffect(() => {
    const onKey = (e) => {
      if (fbOpen || castOpen || dlOpen || picOpen) return;
      if (['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes(e.target.tagName)) return;
      if (e.key === ' ') { e.preventDefault(); toggle(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); jumpSegment(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); jumpSegment(-1); }
      else if (e.key === 'Escape' && onClose) onClose();
      else if (['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown'].includes(e.key)) setFollow(false);
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [toggle, jumpSegment, onClose, fbOpen, castOpen, dlOpen, picOpen]);

  /* ---------------- render ---------------- */
  const STATUS = { rendering: 'rendering…', queued: 'in the queue', failed: 'could not be rendered', none: 'not rendered',
                   skipped: 'in the full read' };
  // A preview (samples, Standard) reads the first N scenes; the full read comes with Deep Focus.
  // What the listener is told about the rest of the script depends on what they can actually do:
  // buy it with credits (Standard owner), upgrade the analysis (sample owner), or neither.
  const previewNote = !preview ? null
    : access?.can_buy
      ? `The first ${preview.scenes} scenes are free. The other ${access.locked.length} cost ${access.credits.scene} credit each, or ${access.credits.full} credits for all of them.`
      : `This preview reads the first ${preview.scenes} scene${preview.scenes === 1 ? '' : 's'}. The full table read — all ${preview.total_scenes} scenes — comes with a Deep Focus analysis.`;
  // They were shown the wall: locked scenes are in the list in front of them. Once per player session,
  // so "nobody bought" can be split into "nobody was offered" and "they were offered and said no".
  // Stripe returns to a URL, not to this overlay — so the URL says "open the read again, here".
  const resumeUrl = useMemo(() => {
    if (typeof location === 'undefined') return null;
    const u = new URL(location.href);
    u.searchParams.set('tableread', (access?.topup?.hash) || '');
    if (sceneNo) u.searchParams.set('trscene', String(sceneNo));
    return u.toString();
  }, [access, sceneNo]);

  const offerSeen = useRef(false);
  useEffect(() => {
    if (!offerSeen.current && recipe && lockedCount > 0) { offerSeen.current = true; track('offer_seen'); }
  }, [recipe, lockedCount, track]);

  const atEnd = !!(recipe && !nextScene && previewNote && !(progress && progress.building));
  const endSeen = useRef(false);
  useEffect(() => { if (atEnd && !endSeen.current) { endSeen.current = true; track('preview_end', { scene: sceneNo }); } }, [atEnd, track, sceneNo]);
  const sceneLabel = (s) => `${s.number} · ${s.title || 'Untitled'}${s.duration_ms ? ` · ${fmt(s.duration_ms)}` : ''}`;
  const waiting = !recipe && !err && !expired && progress && progress.ready === 0;
  // A locked scene stays IN the list (seeing what is behind the wall is the point) and opens the
  // unlock sheet instead of switching to it.
  const pickScene = (n) => {
    const s = scenes.find((x) => x.number === n);
    if (s && s.locked) {
      track('sheet_open', { scene: n });
      if (access?.can_buy || access?.can_unlock_free) setBuy({ scene: n });
      return;
    }
    setSceneNo(n);
  };
  const scenePicker = (
    <select value={sceneNo ?? ''} onChange={(e) => pickScene(+e.target.value)} aria-label="Scene">
      {sceneNo == null && <option value="">—</option>}
      {scenes.map((s) => (
        <option key={s.number} value={s.number} disabled={!s.rendered && !s.locked}>
          {sceneLabel(s)}{s.locked ? ` — 🔒 ${access?.credits?.scene ?? 1} credit` : (s.rendered ? '' : ` — ${STATUS[s.status] ?? 'not ready'}`)}
        </option>
      ))}
    </select>
  );

  return (
    <div className="tr-shell">
      <style>{CSS + CAST_CSS + PICTURE_CSS + TOPUP_CSS}</style>

      <header className="tr-top">
        {onClose
          ? <button className="tr-btn tr-back" onClick={onClose} aria-label="Close">✕<span className="tr-wide"> Close</span></button>
          : backUrl && <a className="tr-btn tr-back" href={backUrl} aria-label="Back to analysis">←<span className="tr-wide"> Back to analysis</span></a>}
        <div className="tr-toptitle">
          <span className="tr-script"><span className="tr-scripttitle">{scriptTitle}</span>
            {!isDemo && <button type="button" className={`tr-alphatag tr-toptag ${alphaHidden ? 'on' : ''}`} onClick={() => hideAlpha(false)}
                                title="Alpha: an early, experimental feature">Alpha</button>}
          </span>
          {isDemo && <span className="tr-demo">placeholder tones · invented lines</span>}
        </div>
        {canDownload && renderedList.length > 0 && <button className="tr-btn tr-fbbtn" onClick={() => setDlOpen(true)}><span className="tr-wide">Download</span><span className="tr-narrow">MP3</span></button>}
        {canEditCast && !expired && <button className="tr-btn tr-fbbtn" onClick={() => setCastOpen(true)}>Cast</button>}
        {!isDemo && <button className="tr-btn tr-fbbtn" onClick={() => setFbOpen(true)}>
          <span className="tr-wide">Send feedback</span><span className="tr-narrow">Feedback</span></button>}
      </header>

      {!isDemo && !alphaHidden && (
        <div className="tr-alpha" role="note">
          <span className="tr-alphatag">Alpha</span>
          <span className="tr-alphatext">Experimental AI voices —{' '}
            <button type="button" className="tr-linkbtn" onClick={() => setFbOpen(true)}>tell us what you hear</button>.</span>
          <button type="button" className="tr-alphax" onClick={() => hideAlpha(true)} aria-label="Hide this notice">✕</button>
        </div>
      )}

      <div className="tr-scroll" ref={scroller}>
        <div className="tr-body">
        {((pick && !expired) || !isDemo) && (
          <aside className="tr-side" aria-label="Picture and notes">
            {pick && !expired && <Picture pick={pick} onBad={markBad} onOpen={() => setPicOpen(true)} />}
            {!isDemo && (
              <p className="tr-sidenote">
                <span className="tr-alphatag">Alpha</span> An early, experimental feature. Voices are AI-generated and
                cast automatically, so expect mispronunciations, flat delivery and the occasional wrong voice.{' '}
                <button type="button" className="tr-linkbtn" onClick={() => setFbOpen(true)}>Tell us what you hear</button>{' '}
                — it shapes what we fix first. Reads are kept for {retentionDays} days after the last listen.
              </p>
            )}
          </aside>
        )}
        <div className="tr-page">
          {notice && <p className="tr-notice" role="status">{notice}</p>}
          {err && <p className="tr-err">{err}</p>}
          {expired && (
            <div className="tr-wait">
              <p className="tr-waith">This table read has expired.</p>
              <p className="tr-dim">Table reads are kept for {retentionDays} days after the last listen. The script and its cast are
                still here, so it can be recorded again{canEditCast ? '' : ' by the script’s owner'}.</p>
              {canEditCast && (
                <button className="tr-btn tr-play tr-rerecord" onClick={() => {
                  axios.post(`${base}/rerecord`).then(() => {
                    setNotice('Recording again. The first scenes will appear here in a few minutes.');
                    loadScenes().catch(() => {});
                    startWatch();
                  }).catch((e) => setErr(e?.response?.data?.error ?? e.message));
                }}>Re-record this read</button>
              )}
            </div>
          )}
          {waiting && (
            <div className="tr-wait">
              <p className="tr-waith">Your table read is being generated.</p>
              <div className="tr-waitbar" role="progressbar" aria-label="Rendering the first scenes" aria-valuetext="in progress"><i /></div>
              <p className="tr-dim">
                {progress.total ? `0 of ${progress.total} scenes ready. ` : ''}
                The first scenes usually take a few minutes. This page checks for new scenes on its own —
                you can leave it open or come back later.
              </p>
            </div>
          )}
          {!err && !recipe && !waiting && !expired && <p className="tr-dim">Loading read…</p>}
          {recipe && (
            <>
              {progress && progress.building && (
                <p className="tr-progress">{progress.ready} of {progress.total} scenes ready — the rest are still rendering.</p>
              )}
              {previewNote && !(progress && progress.building) && sceneNo === renderedList[0]?.number && (
                <p className="tr-progress tr-preview">{previewNote}</p>
              )}
              <p className="tr-hint">Click any line to play from there.</p>
              <ol className="tr-script-list">
                {rows.map((r) => {
                  const on = r.key === activeKey;
                  return (
                    <li key={r.key} ref={(el) => { rowRefs.current[r.key] = el; }}
                        className={`tr-row ${on ? 'on' : ''} ${on && announcing ? 'cue' : ''} ${r.kind === 'narration' ? 'narr' : ''}`}
                        onClick={() => { setFollow(true); seekTo(r.seek_ms); }} title="Play from here">
                      <span className="tr-caret" aria-hidden="true">{on ? '▸' : ''}</span>
                      <span className="tr-rowwho">{r.character ?? ''}</span>
                      <span className="tr-rowtext">{r.text}</span>
                      {r.spanned && <span className="tr-badge" title="Rendered together with the adjacent line">span</span>}
                      {r.method === 'proportional_fallback' &&
                        <span className="tr-badge warn" title="Offset estimated — highlight timing is approximate">est</span>}
                    </li>
                  );
                })}
              </ol>
              {nextScene && (
                <button className="tr-btn tr-nextscene" onClick={() => setSceneNo(nextScene.number)}>
                  Next scene → {sceneLabel(nextScene)}
                </button>
              )}
              {!nextScene && previewNote && !(progress && progress.building) && (
                <div className="tr-endcard">
                  <p className="tr-waith">That’s the end of the preview.</p>
                  <p className="tr-dim">{previewNote}</p>
                  {preview?.upgrade_url && (
                    <a className="tr-btn tr-play tr-upsell" href={preview.upgrade_url}>Get the full analysis</a>
                  )}
                  {access?.can_buy && (
                    <div className="tr-buyrow">
                      <button className="tr-btn tr-play tr-upsell" onClick={() => { track('sheet_open'); setBuy({ scene: 0 }); }}>
                        Unlock the rest — {access.credits.full} credits
                      </button>
                      <button className="tr-btn" onClick={() => { track('sheet_open', { scene: (access.locked || [])[0] }); setBuy({ scene: (access.locked || [])[0] }); }}>
                        Or one scene — {access.credits.scene} credit
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        </div>
        {/* Sticky at the bottom of the SCRIPT (zero height, so it takes no room): it floats over text,
            never over the dock, the seek bar or a button, and the controls never jump. */}
        {!follow && recipe && (
          <div className="tr-followwrap">
            <button className="tr-btn tr-follow" onClick={() => { setFollow(true); scrollToActive(); }}>↓ Follow along</button>
          </div>
        )}
      </div>

      {/* No dock while nothing is playable: a row of disabled controls reads as broken, not as "wait". */}
      {!waiting && !expired && <section className="tr-dock" aria-label="Playback">
        <div className="tr-bar" title="Click to seek" role="slider" tabIndex={0}
             aria-valuenow={Math.round(globalMs)} aria-valuemin={0} aria-valuemax={total_ms}
             onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seekTo(((e.clientX - r.left) / r.width) * total_ms); }}>
          <i style={{ width: `${total_ms ? (globalMs / total_ms) * 100 : 0}%` }} />
        </div>
        <div className="tr-dockin">
          <div className="tr-nowrow">
            {pick && <div className="tr-docpic"><Picture compact pick={pick} onBad={markBad} onOpen={() => setPicOpen(true)} /></div>}
            <div className="tr-nowmain">
              <div className="tr-nowtop">
                {/* The scene picker IS the "where am I" label, at every width: one tap to change scene
                    (it used to be folded behind the ⚙ on phones, where nobody found it). */}
                <span className="tr-nowscene tr-scenepick">{scenePicker}</span>
                <span className="tr-times">{fmt(globalMs)} / {fmt(total_ms)}</span>
              </div>
              <p className={`tr-line ${announcing ? 'cue' : ''} ${items[activeIdx]?.kind === 'narration' ? 'narr' : ''}`} aria-live="polite">
                {activeMember?.character && <b className="tr-who">{activeMember.character}</b>}
                {activeMember?.text ?? '—'}
              </p>
            </div>
          </div>

          <div className="tr-ctlrow">
          <div className="tr-controls">
            <button className="tr-btn" onClick={() => jumpSegment(-1)} title="Previous line (←)" aria-label="Previous line">←</button>
            <button className="tr-btn tr-play" onClick={toggle} disabled={!recipe}>{wantPlay ? 'Pause' : 'Play read'}</button>
            <button className="tr-btn" onClick={() => jumpSegment(1)} title="Next line (→)" aria-label="Next line">→</button>
            <select className="tr-sel" value={rate} aria-label="Speed" onChange={(e) => {
              const v = +e.target.value; track('setting', { detail: `speed=${v}` });
              setRate(v); if (audio.current) audio.current.playbackRate = v; }}>
              {[0.75, 1, 1.25, 1.5].map((v) => <option key={v} value={v}>{v}×</option>)}
            </select>
            <button className={`tr-btn tr-setbtn ${setOpen ? 'on' : ''}`} onClick={() => setSetOpen(!setOpen)}
                    aria-expanded={setOpen} aria-label="Playback settings" title="Scene, narration, speaker names">⚙</button>
          </div>

          <div className={`tr-settings ${setOpen ? 'open' : ''}`}>
            <Seg label="At scene end" value={autoNext ? 'next' : 'stop'} onChange={(v) => { track('setting', { detail: `autonext=${v}` }); setAutoNext(v === 'next'); }}
                 options={[['next', 'Continue'], ['stop', 'Stop']]} />
            <Seg label="Narration" value={settings.narration} onChange={(v) => { track('setting', { detail: `narration=${v}` }); setSettings({ ...settings, narration: v }); }}
                 options={[['full', 'Full'], ['none', 'Off']]} />
            <Seg label="Speaker names" value={settings.speaker_names} onChange={(v) => { track('setting', { detail: `names=${v}` }); setSettings({ ...settings, speaker_names: v }); }}
                 options={[['always', 'Always'], ['first_per_scene', 'First in scene'], ['never', 'Never']]} />
          </div>
          </div>
        </div>
      </section>}

      {buy && (
        <Unlock base={base} scene={buy.scene} access={access} lockedCount={lockedCount} track={track} resumeUrl={resumeUrl}
                onClose={() => setBuy(null)}
                onDone={(msg) => { setBuy(null); setNotice(msg); loadScenes().catch(() => {}); }} />
      )}

      {picOpen && pick && (
        <PictureViewer pick={pick} canGo={canGoToPick} onClose={() => setPicOpen(false)}
                       onGo={() => { setPicOpen(false); setSceneNo(pick.scene); }} />
      )}

      {dlOpen && <Download base={base} settings={settings} retentionDays={retentionDays} onClose={() => setDlOpen(false)} />}

      {castOpen && (
        <CastEditor base={base} onClose={() => setCastOpen(false)}
          onSample={() => { if (wantPlay) { audio.current?.pause(); clearTimeout(gapTimer.current); gapTimer.current = null; setWantPlay(false); } }}
          onSaved={(n) => {
          setNotice(`Recording ${n} cast change${n === 1 ? '' : 's'}. The read updates by itself when it is ready — usually a few minutes.`);
          loadScenes().catch(() => {});
          startWatch();
        }} />
      )}

      {fbOpen && (
        <Feedback url={feedbackUrl} onClose={() => setFbOpen(false)} context={{
          scene: sceneNo, heading, time: fmt(globalMs),
          line: activeMember ? `${activeMember.character ? activeMember.character + ': ' : ''}${activeMember.text}` : null,
        }} />
      )}

      <audio ref={audio} preload="auto"
             onTimeUpdate={(e) => {
               const t = e.target.currentTime; setPosMs(t * 1000);
               if (!e.target.paused && lastT.current != null) {
                 const d = (t - lastT.current) / (e.target.playbackRate || 1);
                 if (d > 0 && d < 1.5) bump(sceneNo, 'ms', d * 1000);      // ignore seeks
               }
               lastT.current = t;
             }}
             onLoadStart={() => { lastT.current = null; }}
             onEnded={() => { if (seg && seg.kind !== 'speaker_name') bump(sceneNo, 'lines', 1); lastT.current = null; advance(); }}
             onPlaying={() => { failStreak.current = 0; }} onError={onAudioError} />
      {/* No onPause handler: assigning src fires 'pause', which would clear the play intent on every transition. */}
      <audio ref={prefetch} preload="auto" muted />
    </div>
  );
}

/**
 * Unlock sheet. `scene` 0 = the rest of the script. The credit is taken when the audio is ready, so
 * this only records the unlock and starts the render; a render that fails costs nothing.
 */
function Unlock({ base, scene, access, lockedCount, track, resumeUrl, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [short, setShort] = useState(false);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    addEventListener('keydown', k, true); return () => removeEventListener('keydown', k, true);
  }, [onClose]);

  const [topup, setTopup] = useState(false);
  const whole = !scene;
  const go = (n) => {
    setBusy(true); setErr(null); setShort(false);
    axios.post(`${base}/unlock`, { scene: n }).then((r) => {
      onDone(r.data.rendering
        ? (n ? `Recording scene ${n}. It appears here when it is ready.` : 'Recording the rest of the script. Scenes appear here as they are ready.')
        : 'Unlocked.');
    }).catch((e) => {
      setBusy(false);
      const isShort = !!e?.response?.data?.credits_short;
      setShort(isShort);
      if (isShort) track?.('short', { scene: n || undefined, credits: e?.response?.data?.cost });
      setErr(e?.response?.data?.error ?? e.message);
    });
  };

  return (
    <div className="tr-fb-backdrop" onClick={onClose}>
      <div className="tr-fb" role="dialog" aria-modal="true" aria-labelledby="tr-un-h" onClick={(e) => e.stopPropagation()}>
        <h2 id="tr-un-h" className="tr-fb-h">{whole ? 'Unlock the rest of the script' : `Unlock scene ${scene}`}</h2>
        <p className="tr-dim">
          The first {access.free_scenes} scenes are free. After that a scene costs{' '}
          <span className="tr-cost">{access.credits.scene} credit</span>, or{' '}
          <span className="tr-cost">{access.credits.full} credits</span> for every scene left
          {lockedCount ? ` (${lockedCount} scene${lockedCount === 1 ? '' : 's'})` : ''}. Unlocked scenes stay
          unlocked on this draft, including after a re-record.
        </p>
        {err && <p className="tr-err">{err}</p>}
        {short && <p className="tr-dim">Craft credits are the ones the analysis gives you for brainstorming.</p>}
        {(short || topup) && access.topup?.checkout_url && <TopUp topup={access.topup} resume={resumeUrl} />}
        <div className="tr-fb-actions">
          <button className="tr-btn" onClick={onClose}>Cancel</button>
          {scene > 0 && (
            <button className="tr-btn" onClick={() => go(scene)} disabled={busy}>
              {busy ? 'Working…' : `This scene — ${access.credits.scene} credit`}
            </button>
          )}
          <button className="tr-btn tr-play" onClick={() => go(0)} disabled={busy}>
            {busy ? 'Working…' : `Everything left — ${access.credits.full} credits`}
          </button>
        </div>
        {!short && access.topup?.checkout_url && !topup && (
          <p className="tr-bal"><button type="button" className="tr-linkbtn" onClick={() => setTopup(true)}>Get more credits</button></p>
        )}
        <p className="tr-bal">You have {access.credits.balance} credit{access.credits.balance === 1 ? '' : 's'}
          {access.can_unlock_free ? ' · admin: unlocking is free' : ''}. Charged when the audio is ready, never before.</p>
      </div>
    </div>
  );
}

/**
 * One setting: a quiet uppercase label + a segmented control. The label and the choices must never
 * look alike (owner: "not obvious what is title and what is option") — the label is small caps and
 * unboxed; every choice is inside the bordered group and the chosen one is filled.
 */
function Seg({ label, value, onChange, options }) {
  const id = useMemo(() => 'trs-' + label.replace(/\W+/g, '').toLowerCase(), [label]);
  return (
    <div className="tr-field" role="group" aria-labelledby={id}>
      <span className="tr-flabel" id={id}>{label}</span>
      <span className="tr-seg">
        {options.map(([v, text]) => (
          <button key={v} type="button" className={v === value ? 'on' : ''} aria-pressed={v === value}
                  onClick={() => onChange(v)}>{text}</button>
        ))}
      </span>
    </div>
  );
}

/**
 * Alpha feedback → the existing feature-request pipeline (/feature-request/submit-api), which emails
 * the owner with the sender attached. The current line is included by default: "this line sounds
 * wrong" is the most useful report and the hardest to describe from memory.
 */
function Feedback({ url = '/feature-request/submit-api', onClose, context }) {
  const [text, setText] = useState('');
  const [aboutLine, setAboutLine] = useState(!!context.line);
  const [state, setState] = useState('idle');     // idle | sending | sent | error
  const box = useRef(null);
  useEffect(() => { box.current?.focus(); }, []);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    addEventListener('keydown', k, true); return () => removeEventListener('keydown', k, true);
  }, [onClose]);

  const send = () => {
    if (!text.trim()) return;
    setState('sending');
    const where = [`[Table read alpha] scene ${context.scene ?? '?'} at ${context.time}`,
                   context.heading ? `heading: ${context.heading}` : null,
                   aboutLine && context.line ? `line: ${context.line}` : null].filter(Boolean).join('\n');
    axios.post(url, {
      feature_request: `${text.trim()}\n\n${where}\n\nSubmitted from: ${window.location.href}`,
    }).then(() => setState('sent')).catch(() => setState('error'));
  };

  return (
    <div className="tr-fb-backdrop" onClick={onClose}>
      <div className="tr-fb" role="dialog" aria-modal="true" aria-labelledby="tr-fb-h" onClick={(e) => e.stopPropagation()}>
        {state === 'sent' ? (
          <>
            <h2 id="tr-fb-h" className="tr-fb-h">Thank you</h2>
            <p className="tr-dim">We read every report. It goes straight to the person building this.</p>
            <div className="tr-fb-actions"><button className="tr-btn tr-play" onClick={onClose}>Back to the read</button></div>
          </>
        ) : (
          <>
            <h2 id="tr-fb-h" className="tr-fb-h">Feedback on the table read</h2>
            <p className="tr-dim">A wrong voice, a mispronounced word, a line read by the wrong character, a scene that
              will not play — anything helps.</p>
            <textarea ref={box} className="tr-fb-text" rows={5} value={text} onChange={(e) => setText(e.target.value)}
                      placeholder="What did you hear?" />
            {context.line && (
              <label className="tr-check tr-fb-line">
                <input type="checkbox" checked={aboutLine} onChange={(e) => setAboutLine(e.target.checked)} />
                <span>About the current line: <i>“{context.line.length > 90 ? context.line.slice(0, 90) + '…' : context.line}”</i></span>
              </label>
            )}
            {state === 'error' && <p className="tr-err">That did not send. Please try again.</p>}
            <div className="tr-fb-actions">
              <button className="tr-btn" onClick={onClose}>Cancel</button>
              <button className="tr-btn tr-play" onClick={send} disabled={!text.trim() || state === 'sending'}>
                {state === 'sending' ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CSS = `
.tr-shell{height:100%;display:flex;flex-direction:column;background:#fff;color:#14536e;
  font-family:inherit;min-height:0;position:relative}
.tr-top{flex:0 0 auto;display:flex;align-items:center;gap:14px;padding:10px 16px;
  border-bottom:1px solid #e3edf1;background:#fff}
.tr-toptitle{display:flex;flex-direction:column;min-width:0;flex:1}
.tr-script{font-weight:650;color:#102d38;font-size:1.02rem;display:flex;align-items:center;min-width:0}
.tr-scripttitle{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
.tr-toptag{flex:0 0 auto}
.tr-demo{font-size:.78rem;color:#5c7a8a}
.tr-back{text-decoration:none;white-space:nowrap}
.tr-fbbtn{white-space:nowrap}
.tr-narrow{display:none}

.tr-alpha{flex:0 0 auto;display:flex;gap:12px;align-items:flex-start;padding:9px 16px;
  background:#fff7e6;border-bottom:1px solid #f0dcae;color:#5a4410;font-size:.86rem;line-height:1.45}
.tr-alphatext{flex:1}
.tr-alphax{flex:0 0 auto;border:0;background:none;color:#8a6a1c;cursor:pointer;font:inherit;padding:0 2px;line-height:1.2}
.tr-alphax:hover{color:#5a4410}
.tr-toptag{margin-left:10px;vertical-align:2px;border:0;cursor:pointer;font-family:inherit}
.tr-sidenote{margin:14px 0 0;font-size:.8rem;line-height:1.5;color:#6b5a2e;background:#fff7e6;border:1px solid #f0dcae;border-radius:8px;padding:9px 11px}
.tr-sidenote .tr-alphatag{font-size:.64rem;padding:1px 5px;margin-right:3px}
.tr-linkbtn{border:0;background:none;padding:0;font:inherit;color:#14536e;text-decoration:underline;cursor:pointer}
.tr-alphatag{flex:0 0 auto;font-weight:700;font-size:.72rem;letter-spacing:.06em;text-transform:uppercase;
  background:#d9a441;color:#fff;border-radius:4px;padding:2px 7px;margin-top:1px}

.tr-scroll{flex:1 1 auto;overflow-y:auto;min-height:0;overscroll-behavior:contain}
.tr-page{max-width:760px;margin:0 auto;padding:18px 16px 68px}
.tr-hint{font-size:.88rem;color:#5c7a8a;margin:0 0 10px}
.tr-progress{font-size:.88rem;color:#14536e;background:#eef6f9;border-radius:8px;padding:8px 12px;margin:0 0 12px}
.tr-notice{font-size:.88rem;color:#14536e;background:#eef6f9;border-radius:8px;padding:8px 12px;margin:0 0 12px}
.tr-wait{padding:24px 0}
.tr-waitbar{height:6px;border-radius:3px;background:#e3edf1;overflow:hidden;margin:14px 0 14px;max-width:420px}
.tr-waitbar>i{display:block;height:100%;width:35%;border-radius:3px;background:#14536e;animation:tr-slide 1.6s ease-in-out infinite}
@keyframes tr-slide{from{transform:translateX(-100%)}to{transform:translateX(290%)}}
@media (prefers-reduced-motion:reduce){.tr-waitbar>i{animation:none;width:100%;opacity:.35}}
.tr-waith{font-size:1.15rem;font-weight:650;color:#102d38;margin:0 0 6px}
.tr-dim{color:#5c7a8a}.tr-err{color:#a4243b}

.tr-script-list{list-style:none;padding:0;margin:0}
.tr-row{display:flex;gap:10px;align-items:baseline;padding:10px 12px 10px 4px;border-radius:8px;
  cursor:pointer;transition:background .15s,color .15s}
.tr-row:hover{background:#f2f7f9}
.tr-row.on{background:#d9ebf3;box-shadow:inset 4px 0 0 #14536e}
.tr-row.on .tr-rowtext{font-weight:600;color:#102d38}
.tr-row.on .tr-rowwho{color:#0f4258}
.tr-row.on.cue .tr-rowtext{color:#587886;font-weight:500}
.tr-row.narr .tr-rowtext{font-style:italic;color:#4d6b79}
.tr-caret{flex:0 0 14px;color:#14536e;font-size:.8rem;line-height:1.5}
.tr-rowwho{flex:0 0 92px;text-align:right;font-weight:650;font-size:.82rem;color:#3d7f9c;letter-spacing:.05em}
.tr-rowtext{flex:1;color:#24424e;font-size:1.05rem;line-height:1.5}
.tr-badge{font-size:.68rem;letter-spacing:.04em;border:1px solid #cfdde4;color:#5c7a8a;border-radius:4px;padding:1px 5px;align-self:center}
.tr-badge.warn{border-color:#c8912f;color:#7a5610}
.tr-nextscene{margin:26px 0 0;width:100%;text-align:left}
.tr-endcard{margin:26px 0 0;padding:16px 18px;border:1px solid #dbe8ee;border-radius:10px;background:#f6fafc}
.tr-endcard .tr-dim{margin:0}
.tr-buyrow{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-top:14px}
.tr-buyrow .tr-upsell{margin-top:0}
.tr-cost{font-weight:650;color:#102d38}
.tr-bal{font-size:.86rem;color:#5c7a8a;margin:10px 0 0}
.tr-upsell{display:inline-block;margin-top:12px;text-decoration:none;flex:0 0 auto}

.tr-dock{position:relative;flex:0 0 auto;background:#fff;border-top:1px solid #dbe8ee;box-shadow:0 -10px 30px -18px rgba(20,83,110,.35)}
.tr-dockin{max-width:760px;margin:0 auto;padding:12px 16px 14px}
.tr-bar{height:5px;background:#e3edf1;cursor:pointer;transition:height .12s}
.tr-bar:hover,.tr-bar:focus-visible{height:10px}
.tr-bar:focus-visible{outline:2px solid #14536e;outline-offset:2px}
.tr-bar>i{display:block;height:100%;background:#14536e;width:0}
.tr-nowtop{display:flex;justify-content:space-between;gap:12px;align-items:baseline}
.tr-heading{font-size:.78rem;font-weight:650;letter-spacing:.04em;color:#5c7a8a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tr-times{color:#5c7a8a;font-size:.78rem;font-variant-numeric:tabular-nums;white-space:nowrap}
.tr-line{margin:4px 0 10px;font-size:1.08rem;line-height:1.4;color:#102d38;font-weight:500;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;transition:color .18s;min-height:1.4em}
.tr-line.cue{color:#6f8c99}
.tr-line.narr{font-style:italic;color:#4d6b79;font-weight:400}
.tr-who{font-style:normal;font-weight:700;font-size:.78rem;letter-spacing:.12em;color:#3d7f9c;margin-right:10px}
.tr-controls{display:flex;gap:8px;align-items:center}
.tr-btn{font:inherit;border:1px solid #cfdde4;background:#fff;color:#14536e;border-radius:8px;padding:9px 14px;cursor:pointer}
.tr-btn:hover{border-color:#14536e}
.tr-btn:focus-visible,.tr-sel:focus-visible,.tr-settings select:focus-visible,.tr-check input:focus-visible,.tr-fb-text:focus-visible{outline:2px solid #14536e;outline-offset:2px}
.tr-play{flex:1;background:#14536e;color:#fff;border-color:#14536e;font-weight:650}
.tr-controls .tr-play{flex:0 0 auto;min-width:150px;white-space:nowrap}
.tr-play:hover{background:#0f4258}
.tr-play:disabled{opacity:.5;cursor:default}
.tr-followwrap{position:sticky;bottom:14px;height:0;display:flex;justify-content:center;align-items:flex-end;pointer-events:none;z-index:2}
.tr-follow{pointer-events:auto;flex:0 0 auto;background:#fff7e6;border-color:#d9a441;
  color:#6b4a0c;font-weight:600;white-space:nowrap;padding:7px 14px;border-radius:999px;box-shadow:0 6px 18px -8px rgba(16,45,56,.45)}
.tr-sel,.tr-settings select{font:inherit;border:1px solid #cfdde4;border-radius:8px;color:#14536e;background:#fff;padding:8px}
.tr-settings{display:flex;gap:10px 20px;align-items:center;flex-wrap:wrap;margin-top:10px}
.tr-field{display:flex;align-items:center;gap:8px;margin:0}
.tr-flabel{font-size:.68rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#4d6b79;white-space:nowrap}
.tr-seg{display:inline-flex;border:1px solid #cfdde4;border-radius:8px;overflow:hidden;background:#fff}
.tr-seg button{font:inherit;font-size:.84rem;border:0;background:none;color:#3d6474;padding:5px 11px;cursor:pointer;white-space:nowrap}
.tr-seg button+button{border-left:1px solid #cfdde4}
.tr-seg button:hover{background:#f2f7f9}
.tr-seg button.on{background:#d9ebf3;color:#0f4258;font-weight:650;box-shadow:inset 0 -2px 0 #14536e}
.tr-seg button:focus-visible{outline:2px solid #14536e;outline-offset:-2px}
.tr-settings label{display:flex;gap:8px;align-items:center;font-size:.84rem;color:#5c7a8a}
.tr-settings select{padding:5px 8px;font-size:.84rem}
.tr-scenepick select{max-width:340px}
.tr-nowscene{flex:0 0 auto}
.tr-nowscene select{font:inherit;font-size:.84rem;font-weight:600;border:1px solid #cfdde4;border-radius:8px;
  color:#14536e;background:#fff;padding:4px 8px;max-width:min(62vw,320px)}
.tr-check{cursor:pointer}

.tr-fb-backdrop{position:absolute;inset:0;background:rgba(16,45,56,.38);display:flex;align-items:center;justify-content:center;padding:16px;z-index:5}
.tr-fb{background:#fff;border-radius:12px;max-width:520px;width:100%;padding:20px 22px;box-shadow:0 20px 50px -20px rgba(16,45,56,.5)}
.tr-fb-h{font-size:1.15rem;font-weight:650;color:#102d38;margin:0 0 6px}
.tr-fb-text{width:100%;font:inherit;border:1px solid #cfdde4;border-radius:8px;padding:10px;color:#102d38;margin:10px 0 8px;resize:vertical}
.tr-fb-line{display:flex;gap:8px;align-items:flex-start;font-size:.86rem;color:#5c7a8a}
.tr-fb-line input{margin-top:3px}
.tr-fb-actions{display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;margin-top:14px}
.tr-fb-actions .tr-play{flex:0 0 auto;text-decoration:none}
.tr-dl-note{font-size:.82rem;margin:10px 0 0;text-align:right}
.tr-rerecord{margin-top:14px;flex:0 0 auto}
/* WIDE: the notice lives in the side column (no banner row), and the dock is two rows — now
   playing on one line, then transport + settings on one (wrapping) line. */
@media (min-width:1080px){
  .tr-alpha{display:none}
  .tr-toptag{cursor:default;pointer-events:none}
  .tr-dockin{max-width:1360px;padding:8px 20px 10px}
  .tr-scenepick select{max-width:260px}
  .tr-narrowonly{display:none !important}
  .tr-nowscene{order:1;flex:0 0 auto}
  .tr-heading{display:none}
  .tr-nowscene select{font:inherit;font-size:.84rem;font-weight:600;border:1px solid #cfdde4;border-radius:8px;color:#14536e;background:#fff;padding:4px 8px}
  .tr-nowrow{align-items:center}
  .tr-nowmain{display:flex;align-items:baseline;gap:14px}
  .tr-nowtop{display:contents}
  .tr-line{order:2;flex:1;margin:0;-webkit-line-clamp:1;font-size:1rem}
  .tr-times{order:3}
  .tr-ctlrow{display:flex;align-items:center;gap:10px 22px;flex-wrap:wrap;margin-top:8px}
  .tr-ctlrow .tr-settings{margin-top:0}
  .tr-controls .tr-btn{padding:7px 12px}
}
@media (min-width:1600px){
  .tr-dockin{max-width:1640px}
}
@media (max-width:1079px){
  .tr-side .tr-sidenote{display:none}
}
.tr-setbtn{display:none}
@media (max-width:1079px){ .tr-wideonly{display:none !important} }
@media (max-width:560px){
  .tr-toptag:not(.on){display:none}
  .tr-setbtn{display:inline-block;padding:7px 11px}
  .tr-controls{flex-wrap:wrap}
  .tr-controls .tr-play{flex:1 1 90px;min-width:0}
  .tr-controls .tr-btn:not(.tr-play){padding:7px 11px}
  .tr-setbtn.on{background:#eef6f9;border-color:#14536e}
  .tr-settings:not(.open){display:none}
  .tr-nowtop{gap:6px}
  .tr-nowscene select{max-width:52vw}
  .tr-settings{gap:8px 14px}
  .tr-field{flex-wrap:wrap;gap:4px 8px}
  .tr-seg button{padding:5px 9px}
  .tr-rowwho{flex-basis:62px}
  .tr-dockin{padding:10px 12px 12px}
  .tr-line{font-size:1rem}
  .tr-scenepick select{max-width:220px}
  .tr-alpha{font-size:.8rem;padding:7px 12px}
  .tr-wide{display:none}
  .tr-narrow{display:inline}
  .tr-top{gap:8px;padding:8px 12px}
  .tr-back,.tr-fbbtn{padding:7px 10px}
}
`;
